import { Search, X } from 'lucide-react'
import styled from 'styled-components'
import { MOBILE_BREAKPOINT_PX } from 'utils/mobileLayout'

export const Bar = styled.div`
  position: relative;
  z-index: 20;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  flex-shrink: 0;
  overflow: visible;
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
  min-width: 0;
  position: relative;
`

export const TextInput = styled.input`
  height: 2.5rem;
  padding: 0 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid ${(p) => p.theme.forecast.border};
  background: ${(p) => p.theme.forecast.cardBackground};
  font-size: 1rem;
  color: ${(p) => p.theme.forecast.defaultText};
  outline: none;
  width: 100%;
  transition: border-color 0.15s ease;

  @media (min-width: ${MOBILE_BREAKPOINT_PX}px) {
    height: 1.75rem;
    font-size: 0.75rem;
  }

  &:focus {
    border-color: ${(p) => p.theme.forecast.primary}88;
  }

  &::placeholder {
    color: ${(p) => p.theme.forecast.secondaryText};
    opacity: 0.5;
  }
`

export const MultiSelectTrigger = styled.button<{ $hasValue?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 2.5rem;
  padding: 0 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid
    ${(p) =>
      p.$hasValue ? p.theme.forecast.primary + '44' : p.theme.forecast.border};
  background: ${(p) => p.theme.forecast.cardBackground};
  font-size: 1rem;
  color: ${(p) => p.theme.forecast.defaultText};
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: border-color 0.15s ease;

  @media (min-width: ${MOBILE_BREAKPOINT_PX}px) {
    height: 1.75rem;
    font-size: 0.75rem;
  }

  &:hover {
    border-color: ${(p) => p.theme.forecast.primary + '44'};
  }
`

export const TriggerText = styled.span<{ $placeholder?: boolean }>`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  opacity: ${(p) => (p.$placeholder ? 0.5 : 1)};
`

export const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: ${(p) => p.theme.forecast.secondaryText};
  padding: 0;
  flex-shrink: 0;

  &:hover {
    color: ${(p) => p.theme.forecast.defaultText};
  }
`

export const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 30;
  margin-top: 0.25rem;
  display: flex;
  flex-direction: column;
  min-width: 12rem;
  max-height: 16rem;
  border-radius: 0.5rem;
  background: ${(p) => p.theme.forecast.glassBackground};
  backdrop-filter: blur(32px);
  -webkit-backdrop-filter: blur(32px);
  box-shadow:
    0 0.0625rem 0.125rem 0 ${(p) => p.theme.forecast.boxShadow},
    0 0.25rem 0.5rem 0 ${(p) => p.theme.forecast.boxShadow};
`

export const DropdownSearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-bottom: 1px solid ${(p) => p.theme.forecast.border};
  flex-shrink: 0;
`

export const DropdownSearchIcon = styled(Search)`
  height: 0.75rem;
  width: 0.75rem;
  color: ${(p) => p.theme.forecast.secondaryText};
  flex-shrink: 0;
`

export const DropdownSearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  color: ${(p) => p.theme.forecast.defaultText};
  width: 100%;
  min-width: 6rem;
  padding: 0;

  @media (min-width: ${MOBILE_BREAKPOINT_PX}px) {
    font-size: 0.8125rem;
  }

  &::placeholder {
    color: ${(p) => p.theme.forecast.secondaryText};
    opacity: 0.6;
  }
`

export const DropdownList = styled.div`
  overflow-y: auto;
  max-height: 13rem;
  padding: 0.25rem 0;
`

export const DropdownEmpty = styled.div`
  display: flex;
  align-items: center;
  min-height: 2.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(p) => p.theme.forecast.secondaryText};
  opacity: 0.5;
`

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-height: 2.5rem;
  padding: 0.25rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${(p) => p.theme.forecast.defaultText};

  @media (min-width: ${MOBILE_BREAKPOINT_PX}px) {
    min-height: 0;
    font-size: 0.8125rem;
  }

  &:hover {
    background: ${(p) => p.theme.forecast.hoverButton};
  }
`

export const Checkbox = styled.input`
  width: 0.875rem;
  min-width: 0.875rem;
  height: 0.875rem;
  min-height: 0.875rem;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: ${(p) => p.theme.forecast.primary};
`

export const CloseIcon = styled(X)`
  height: 0.625rem;
  width: 0.625rem;
`
